import { SelectionState, SurveyData } from '../../DTOs';
import { SurveyAnswer } from '../../DTOs/survey/SurveyAnswer';

// Progress through the survey answer is tracked using an array of selection states for each question.
// possible selection states:
// missing / set (unconditional questions)
// conditional-missing / conditional-set / conditional-skipped (conditional questions)
//
// When state is fetched without an explicit target, the respondent resumes at the introduction
// for a fresh answer, otherwise at the first unanswered question, otherwise done. The client
// derives all navigation and progress from the SelectionState included in the status response,
// and requests a concrete stage (intro / segmentations / a question) for every other transition.
// Client fetches state on each interaction with the survey.

export const surveyAnswerStateModule = {
    isQuestionActive,
    computeResumeStage,
    computeSelectionState
};

// A question is active when it is part of the answer flow, i.e. not a skipped conditional.
function isQuestionActive(selectionState: SelectionState, questionIndex: number): boolean {
    const selection = selectionState.selections[questionIndex];
    return selection !== undefined && selection.state !== 'skipped';
}

// Resolves where the respondent lands when no explicit target is requested.
// A fresh answer (segmentations missing, or nothing answered in a survey
// without segmentations) lands on the introduction. Otherwise the first
// unanswered question. A completed answer resumes from the last submitted
// action - the following answered question - so a respondent mid-resubmission
// continues where they left off; an answer whose last action was the final
// question lands on done. Derived purely from the selection state and last action.
type SurveyStage =
    | { stage: 'intro' }
    | { stage: 'question', questionIndex: number }
    | { stage: 'done' };
function computeResumeStage(
    survey: SurveyData,
    answer: SurveyAnswer
): SurveyStage {
    const selectionState = computeSelectionState(survey, answer);

    const fresh =
        selectionState.segmentations === 'missing' ||
        (selectionState.segmentations === 'not-applicable' &&
            !selectionState.selections.some(s => s.state === 'set') &&
            selectionState.selections.length > 0);
    if (fresh)
        return { stage: 'intro' };

    const firstMissingIndex = selectionState.selections.findIndex(s => s.state === 'missing');
    if (firstMissingIndex !== -1)
        return { stage: 'question', questionIndex: firstMissingIndex };

    // Nothing is missing: resume from the last submitted action.
    if (answer.lastAction !== undefined) {
        const resumeIndex = answer.lastAction.name === 'segments'
            ? firstActiveQuestionIndex(selectionState)
            : nextActiveQuestionIndex(selectionState, answer.lastAction.questionIndex);
        if (resumeIndex !== -1)
            return { stage: 'question', questionIndex: resumeIndex };
    }

    return { stage: 'done' };
}

// Pure computation of the per-question selection states. Survey availability
// (start/end window, has questions) is checked by the calling query via
// getSurveyStatus(survey).canSubmitAnswer and is intentionally not repeated here.
function computeSelectionState(
    survey: SurveyData,
    answer: Pick<SurveyAnswer, 'segments' | 'selections'>
): SelectionState {
    const state: SelectionState = {
        segmentations:'not-applicable',
        selections: []
    };
    if(survey.segmentations.length > 0)
    {
        if(answer.segments.length === 0)
            state.segmentations = 'missing';
        else
            state.segmentations = 'set';
    }

    const allQuestions = survey.themes.flatMap(t => t.questions);

    let prevUnconditionalIndex = -1;
    for(let i=0; i<allQuestions.length; i++)
    {
        const q = allQuestions[i];

        // Primary question - must be answered before moving on
        if(!q.conditionalDisplay)
        {
            if(answer.selections[i] === undefined || answer.selections[i].values.length === 0)
            {
                state.selections.push({ conditional: false, state: 'missing' });
            }
            else  {
                state.selections.push({ conditional: false, state: 'set' });
            }

            prevUnconditionalIndex = i;
        }

        // Conditional question - only active if trigger values are selected
        else {
            if(prevUnconditionalIndex === -1)
                throw new Error("No primary question preceeds the conditional question (index:" + i + ")");

            const triggerValues = q.conditionalDisplay.triggerValues;
            const triggerSelection = answer.selections[prevUnconditionalIndex]?.values || [];
            const triggerSelected = triggerSelection.some(value => triggerValues.includes(value));
            if(triggerSelected){
                if(answer.selections[i] === undefined || answer.selections[i].values.length === 0)
                {
                    state.selections.push({ conditional: true, state: 'missing' });
                }
                else {
                    state.selections.push({ conditional: true, state: 'set' });
                }

            }
            else {
                state.selections.push({ conditional: true, state: 'skipped' });
                // No action needed for not-applicable conditional questions
            }
        }
    }

    return state;
}

function firstActiveQuestionIndex(selectionState: SelectionState): number {
    for (let i = 0; i < selectionState.selections.length; i++) {
        if (isQuestionActive(selectionState, i))
            return i;
    }
    return -1;
}

function nextActiveQuestionIndex(selectionState: SelectionState, fromIndex: number): number {
    for (let i = fromIndex + 1; i < selectionState.selections.length; i++) {
        if (isQuestionActive(selectionState, i))
            return i;
    }
    return -1;
}